/**
 * src/lib/docs/claude.ts
 *
 * Builds the `CLAUDE.md` file content for the scaffolded project.
 * This document provides Claude with project-specific context including
 * architecture patterns, key file locations, and critical rules.
 */

import { runScript } from '../pm';
import type { AgentDocsOptions } from './agents';
import { dbLabel, migrationCmd } from './agents';

/**
 * Returns a DB model code example for use in documentation.
 *
 * @param db         - The chosen database/ORM.
 * @param moduleName - PascalCase module name to substitute into the example.
 */
function modelExample(db: AgentDocsOptions['db'], moduleName: string): string {
  const lower = moduleName.toLowerCase();

  switch (db) {
    case 'mongoose':
      return `\`\`\`ts
// src/app/modules/${moduleName}/${lower}.model.ts
import { Schema, model } from 'mongoose';
import { I${moduleName} } from './${lower}.interface';

const ${lower}Schema = new Schema<I${moduleName}>(
  {
    name: { type: String, required: true },
    // ... fields
  },
  { timestamps: true },
);

export const ${moduleName} = model<I${moduleName}>('${moduleName}', ${lower}Schema);
\`\`\``;

    case 'prisma':
      return `\`\`\`prisma
// prisma/schema.prisma
model ${moduleName} {
  id        String   @id @default(cuid())
  name      String
  createdAt DateTime @default(now())
  updatedAt DateTime @updatedAt
}
\`\`\`

Then access via:
\`\`\`ts
import { prisma } from '../../utils/prisma';
const items = await prisma.${lower}.findMany();
\`\`\``;

    case 'drizzle':
      return `\`\`\`ts
// src/app/modules/${moduleName}/${lower}.model.ts
import { pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import crypto from 'crypto';

export const ${lower}s = pgTable('${lower}s', {
  id:        text('id').primaryKey().$defaultFn(() => crypto.randomUUID()),
  name:      varchar('name', { length: 255 }).notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});
\`\`\``;

    default:
      return '';
  }
}

/**
 * Returns a validation schema code example for use in documentation.
 *
 * @param validator  - The chosen validation library.
 * @param moduleName - PascalCase module name.
 */
function validationExample(
  validator: AgentDocsOptions['validator'],
  moduleName: string,
): string {
  const lower = moduleName.toLowerCase();

  if (validator === 'zod') {
    return `\`\`\`ts
// src/app/modules/${moduleName}/${lower}.validation.ts
import { z } from 'zod';

const create${moduleName}Schema = z.object({
  body: z.object({
    name: z.string().min(1, 'Name is required'),
  }),
});

export const ${moduleName}Validation = { create${moduleName}Schema };
\`\`\``;
  }

  return `\`\`\`ts
// src/app/modules/${moduleName}/${lower}.validation.ts
import Joi from 'joi';

const create${moduleName}Schema = Joi.object({
  name: Joi.string().required().min(1),
});

export const ${moduleName}Validation = { create${moduleName}Schema };
\`\`\``;
}

/**
 * Builds the full `CLAUDE.md` content as a string.
 *
 * @param opts - Project configuration options chosen during scaffolding.
 * @returns The complete markdown document.
 */
export function buildClaudeMd(opts: AgentDocsOptions): string {
  const {
    projectName,
    db,
    validator,
    useAuth,
    useDocker,
    tokenDelivery,
    pm = 'npm',
  } = opts;

  const lines: string[] = [];

  lines.push('# CLAUDE.md');
  lines.push('');
  lines.push(`> Project-specific context for Claude. Auto-generated by [create-express-modular](https://create-express-modular.lovable.app/).`);
  lines.push('');

  lines.push(`## Overview`);
  lines.push('');
  lines.push(`**${projectName}** is a production-ready Express + TypeScript backend built with the \`create-express-modular\` (CEM) CLI.`);
  lines.push('');
  lines.push(`| Layer | Choice |`);
  lines.push(`|---|---|`);
  lines.push(`| Database / ORM | ${dbLabel(db)} |`);
  lines.push(`| Validation | ${validator === 'zod' ? 'Zod' : 'Joi'} |`);
  lines.push(`| Package Manager | ${pm} |`);
  if (useAuth) {
    const delivery = tokenDelivery === 'cookie' ? 'HTTP-only cookies' : 'Authorization header';
    lines.push(`| Auth | JWT + bcrypt (${delivery}) |`);
  }
  if (useDocker) {
    lines.push(`| Docker | Yes |`);
  }
  lines.push('');

  lines.push('## Project Structure');
  lines.push('');
  lines.push('```');
  lines.push('cem-cli.json                      # Project configuration & stack manifest');
  lines.push('src/');
  lines.push('├── app/');
  lines.push('│   ├── config/index.ts           # Centralized config — ALL env vars');
  lines.push('│   ├── errors/                    # AppError + DB-specific error handlers');
  lines.push('│   ├── interfaces/                # Shared TypeScript types');
  lines.push('│   ├── middlewares/                # <name>.middleware.ts (strict naming)');
  lines.push('│   ├── modules/                   # Domain modules (<Name>/ directories)');
  lines.push('│   ├── routes/index.ts            # Auto-wired route registry');
  lines.push('│   └── utils/                     # catchAsync, sendResponse, validateRequest, logger');
  lines.push('├── app.ts                         # Express app setup');
  lines.push('└── server.ts                      # DB connection + server bootstrap');
  lines.push('```');
  lines.push('');

  lines.push('## Module Anatomy');
  lines.push('');
  lines.push('Every feature module lives in `src/app/modules/<Name>/` and contains:');
  lines.push('');
  lines.push('| File | Purpose |');
  lines.push('|---|---|');
  lines.push('| `<name>.interface.ts` | TypeScript interface / types |');
  lines.push('| `<name>.model.ts` | Database model / schema |');
  lines.push('| `<name>.validation.ts` | Request validation schemas |');
  lines.push('| `<name>.service.ts` | Business logic (DB operations) |');
  lines.push('| `<name>.controller.ts` | Request handlers (uses `catchAsync` + `sendResponse`) |');
  lines.push('| `<name>.route.ts` | Express router |');
  lines.push('| `<name>.constant.ts` | *(optional)* ENUMs, searchable fields |');
  lines.push('| `<name>.utils.ts` | *(optional)* Module-specific helpers |');
  lines.push('');

  lines.push('## Key Patterns');
  lines.push('');

  lines.push('### Controller Pattern');
  lines.push('');
  lines.push('```ts');
  lines.push("import { Request, Response } from 'express';");
  lines.push("import { catchAsync } from '../../utils/catchAsync';");
  lines.push("import sendResponse from '../../utils/sendResponse';");
  lines.push("import { StatusCodes } from 'http-status-codes';");
  lines.push('');
  lines.push('const createItem = catchAsync(async (req: Request, res: Response) => {');
  lines.push('  const result = await ItemService.create(req.body);');
  lines.push('  sendResponse(res, {');
  lines.push('    statusCode: StatusCodes.CREATED,');
  lines.push('    success: true,');
  lines.push("    message: 'Item created successfully',");
  lines.push('    data: result,');
  lines.push('  });');
  lines.push('});');
  lines.push('```');
  lines.push('');

  lines.push('### Model Pattern');
  lines.push('');
  lines.push(modelExample(db, 'Item'));
  lines.push('');

  lines.push('### Validation Pattern');
  lines.push('');
  lines.push(validationExample(validator, 'Item'));
  lines.push('');

  lines.push('### Route Pattern');
  lines.push('');
  lines.push('```ts');
  lines.push("import express from 'express';");
  lines.push("import { ItemControllers } from './item.controller';");
  lines.push("import validateRequest from '../../utils/validateRequest';");
  lines.push("import { ItemValidation } from './item.validation';");
  if (useAuth) {
    lines.push("import auth from '../../middlewares/auth.middleware';");
  }
  lines.push('');
  lines.push('const router = express.Router();');
  lines.push('');
  if (useAuth) {
    lines.push('router.post(');
    lines.push("  '/,");
    lines.push("  auth('USER'),");
    lines.push('  validateRequest(ItemValidation.createItemSchema),');
    lines.push('  ItemControllers.createItem,');
    lines.push(');');
    lines.push("router.get('/', auth('USER', 'ADMIN'), ItemControllers.getAllItems);");
  } else {
    lines.push('router.post(');
    lines.push("  '/,");
    lines.push('  validateRequest(ItemValidation.createItemSchema),');
    lines.push('  ItemControllers.createItem,');
    lines.push(');');
    lines.push("router.get('/', ItemControllers.getAllItems);");
  }
  lines.push('');
  lines.push('export const ItemRoutes = router;');
  lines.push('```');
  lines.push('');

  lines.push('## Environment Variables');
  lines.push('');
  lines.push('All env vars are centralized in `src/app/config/index.ts`. To add a new one:');
  lines.push('');
  lines.push('```bash');
  lines.push('cem add env STRIPE_SECRET_KEY');
  lines.push('```');
  lines.push('');
  lines.push("This updates `.env`, `.env.example`, and `config/index.ts` in one step. Then import:");
  lines.push('');
  lines.push('```ts');
  lines.push("import config from './app/config';");
  lines.push('console.log(config.stripe_secret_key);');
  lines.push('```');
  lines.push('');

  if (useAuth) {
    lines.push('## Authentication');
    lines.push('');
    lines.push(`This project uses JWT authentication with **${tokenDelivery === 'cookie' ? 'HTTP-only cookie' : 'Authorization header'}** token delivery.`);
    lines.push('');
    lines.push('### Protecting a Route');
    lines.push('');
    lines.push('```ts');
    lines.push("import auth from '../../middlewares/auth.middleware';");
    lines.push('');
    lines.push('// Single role');
    lines.push("router.get('/admin', auth('ADMIN'), controller.adminOnly);");
    lines.push('');
    lines.push('// Multiple roles');
    lines.push("router.get('/dashboard', auth('USER', 'ADMIN'), controller.dashboard);");
    lines.push('```');
    lines.push('');
    lines.push('### Core auth files');
    lines.push('');
    lines.push('- `src/app/modules/Auth/` — Controller, service, model, validation, route');
    lines.push('- `src/app/middlewares/auth.middleware.ts` — JWT verification + role guard');
    lines.push('- `src/app/middlewares/rateLimiter.middleware.ts` — Global + login rate limiters');
    lines.push('');
  }

  lines.push('## Build & Quality');
  lines.push('');
  lines.push('| Command | What it does |');
  lines.push('|---|---|');
  lines.push('| `cem dev` | Start dev server (tsx, hot reload) |');
  lines.push('| `cem build` | Middleware naming guard → Architecture guard → `tsc` |');
  lines.push('| `cem check` | Type-check → ESLint → Prettier |');
  lines.push('| `cem start` | Production server with preflight checks |');
  lines.push(`| \`${runScript(pm, 'lint:fix')}\` | Auto-fix lint issues |`);
  lines.push(`| \`${runScript(pm, 'prettier:fix')}\` | Auto-format code |`);
  lines.push('');

  const migrate = migrationCmd(db, pm);
  if (migrate) {
    lines.push('## Database Migrations');
    lines.push('');
    lines.push('```bash');
    lines.push(migrate);
    lines.push('```');
    lines.push('');
  }

  if (useDocker) {
    lines.push('## Docker');
    lines.push('');
    lines.push('```bash');
    lines.push('# Dev (app + database sidecar)');
    lines.push('docker-compose up --build');
    lines.push('');
    lines.push('# Production');
    lines.push(`docker build -t ${projectName} .`);
    lines.push(`docker run -p 5000:5000 --env-file .env ${projectName}`);
    lines.push('```');
    lines.push('');
  }

  lines.push('## Critical Rules');
  lines.push('');
  lines.push('- **Use CEM CLI** for scaffolding modules, middleware, and env vars — it handles file creation, naming, and route wiring automatically.');
  lines.push('- **Never use `process.env` directly** — always import from `src/app/config`.');
  lines.push('- **Middleware naming is enforced** — `<name>.middleware.ts` is mandatory. `cem build` rejects non-conforming files.');
  lines.push('- **Do not remove inject markers** in `src/app/routes/index.ts` (`// --- INJECT IMPORTS HERE ---` and `// --- INJECT ROUTES HERE ---`).');
  lines.push('- **Error handling is automatic** — the `globalErrorHandler` middleware maps ' + db + ' and ' + validator + ' errors to a consistent `{ success, message, errorSources }` JSON response.');
  lines.push('- **`catchAsync` is required** — all async Express handlers must be wrapped with it to avoid unhandled promise rejections.');
  lines.push('');

  return lines.join('\n');
}
